import { useRef, useState } from "react";
import { useApolloClient, useQuery } from "@apollo/client/react";
import { toast } from "sonner";
import {
    BarChart,
    Body,
    Button,
    Checkbox,
    Container,
    DataTable,
    type DataTableColumn,
    Heading,
    Label,
    PieChart,
    SectionHeader,
    StatCard,
    StatCardGroup,
    Text,
} from "@athena/ui";
import { OrganizationOptionsDocument } from "@/graphql/organizations.generated";
import { OrganizationActivityStatsDocument, type OrganizationActivityStatsQuery } from "@/graphql/userAction.generated";
import { OrganizationLmsStatsDocument } from "@/graphql/assignments.generated";
import { OrganizationMembershipStatsDocument } from "@/graphql/organizations.generated";
import { defaultTimeFrame, TimeFrameSelector, utcWindow, type TimeFrame } from "@/components/TimeFrameSelector";
import { exportCsv } from "@/lib/exportCsv";
import { downloadXlsx, type Sheet } from "@/lib/xlsx";
import { flattenOrgOptions } from "@/lib/organizationOptions";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { OrganizationStatsExportDocument } from "@/graphql/statsExport.generated";
import React from "react";
import {
    dailyActions,
    engagementCategoryLabelMap,
    formatDayLabel,
    formatMonthLabel,
    frequencyTierLabelMap,
    frequencyTierDescriptionMap,
    hasAnyValue,
    NoChartData,
    ofMembers,
    pct,
    clientPlatforms,
    platformLabel,
    rosterTotal,
    topVideoTags,
} from "./statsHelpers";
import { buildStatsCsvRows, STATS_CSV_HEADERS } from "./statsCsvExport";
import { OrganizationScopeSelect } from "@/components/OrganizationScopeSelect";
import { isAllSelected, offersAll, orgLabel, scopeLabel } from "./statsScope";
import {
    ALL_REPORT_KEYS,
    includedSectionCount,
    reportSection,
    sectionCheckState,
    sectionKeys,
    withKeys,
    type ReportItemId,
    type ReportKey,
    type ReportSectionId,
} from "./statsReport/reportSections";
import { emptyNarrative, reportBrand, type ReportBrandSlug, type ReportNarrative } from "./statsReport/reportContent";
import { ReportContentPanel } from "./statsReport/ReportContentPanel";
import { PartnerReport } from "./statsReport/PartnerReport";
import { exportReportPdf } from "./statsReport/exportReportPdf";

/**
 * Where the report is mounted for its capture: laid out at full size, but off to the side of the
 * page where nobody sees it. html2canvas renders it from its own bounds, so it need not be on screen.
 */
const OFFSCREEN_REPORT_LEFT_PX = -10_000;

type VideoViewSummary = OrganizationActivityStatsQuery["organizationActivityStats"]["videoViewSummaries"][number];

const VIDEO_TAG_COLUMNS: DataTableColumn<{ tag: string; views: number }>[] = [
    { key: "tag", header: "Category Name", cell: (tag) => tag.tag },
    { key: "views", header: "Views", cell: (tag) => tag.views },
];

const VIDEO_TOPIC_COLUMNS: DataTableColumn<VideoViewSummary>[] = [
    { key: "title", header: "Title", cell: (video) => video.title },
    { key: "views", header: "Views", cell: (video) => video.views },
    { key: "unique", header: "Unique viewers", cell: (video) => video.uniqueViewers },
];

/**
 * Organizations read at once while building the Excel export. The GraphQL Lambda serves ten
 * requests at a time for every user of the platform and refuses the rest, so the export keeps to
 * two and leaves room for everyone else while it runs.
 */
const EXPORT_CONCURRENCY = 2;

/** Tries per organization before the export gives up: a refused read usually goes through a moment later. */
const EXPORT_ATTEMPTS = 3;
const EXPORT_RETRY_DELAY_MS = 1_000;

/**
 * How long the selection must sit still before the page reads it, so checking several boxes in a
 * row costs one read rather than one per box, each holding one of those ten places for seconds.
 */
const SELECTION_SETTLE_MS = 400;

/** Runs a read, and runs it again after a growing pause if it is refused or fails. */
const withRetries = async <T,>(read: () => Promise<T>): Promise<T> => {
    for (let attempt = 1; ; attempt += 1) {
        try {
            return await read();
        } catch (error) {
            if (attempt >= EXPORT_ATTEMPTS) throw error;
            await new Promise((resolve) => setTimeout(resolve, EXPORT_RETRY_DELAY_MS * attempt));
        }
    }
};

export const StatsPage: React.FC = () => {
    const { data: orgData } = useQuery(OrganizationOptionsDocument);
    const apollo = useApolloClient();
    /**
     * The organizations read, as one population (ATH-247). One, as the page always read; several,
     * chosen with checkboxes once the dropdown is switched to multiple; or all of them.
     */
    const [
        selectedOrgIds,
        setSelectedOrgIds,
    ] = useState<string[]>([]);
    // What the page reads: the selection once it has settled.
    const queriedOrgIds = useDebouncedValue(selectedOrgIds, SELECTION_SETTLE_MS);
    const [
        exportProgress,
        setExportProgress,
    ] = useState<{ done: number; total: number } | undefined>(undefined);
    const [
        timeFrame,
        setTimeFrame,
    ] = useState<TimeFrame>(defaultTimeFrame());
    /** The sections and sub-sections chosen for the PDF — see `REPORT_SECTIONS`. */
    const [
        selectedKeys,
        setSelectedKeys,
    ] = useState<Set<ReportKey>>(new Set());
    const [
        brand,
        setBrand,
    ] = useState<ReportBrandSlug>("RELEASE_U");
    const [
        narrative,
        setNarrative,
    ] = useState<ReportNarrative>(emptyNarrative());
    const [
        isExportingPdf,
        setIsExportingPdf,
    ] = useState<boolean>(false);
    const [
        includeTagViews,
        setIncludeTagViews,
    ] = useState<boolean>(true);
    const [
        includeTopicViews,
        setIncludeTopicViews,
    ] = useState<boolean>(true);
    const reportRef = useRef<HTMLDivElement | null>(null);

    /**
     * Mounts the report off-screen, captures it, and lays it onto letter pages. The report is only
     * rendered while this runs, so the page pays nothing for it the rest of the time.
     */
    const handleExportPdf = async () => {
        setIsExportingPdf(true);
        try {
            // Let React commit the report and the browser lay it out before it is measured.
            await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
            const root = reportRef.current;
            if (!root) return;
            await exportReportPdf(root, `stats-${timeFrame.label.replace(/\s+/g, "-")}.pdf`);
        } catch {
            toast.error("Couldn't build the PDF. Try again.");
        } finally {
            setIsExportingPdf(false);
        }
    };

    /** Chooses or clears every sub-section at once; a dash means some of them are chosen. */
    const sectionCheckbox = (id: ReportSectionId, label: string) => {
        const section = reportSection(id);
        return (
            <Checkbox
                checked={sectionCheckState(section, selectedKeys)}
                onCheckedChange={(checked) =>
                    setSelectedKeys((prev) => withKeys(prev, sectionKeys(section), checked === true))
                }
                aria-label={`Include ${label} in PDF export`}
            />
        );
    };

    /** One stat card, chosen on its own. */
    const itemCheckbox = (id: ReportItemId, label: string) => (
        <Checkbox
            checked={selectedKeys.has(id)}
            onCheckedChange={(checked) => setSelectedKeys((prev) => withKeys(prev, [id], checked === true))}
            aria-label={`Include ${label} in PDF export`}
        />
    );

    // One window for every query on the page, so the sections cannot disagree about the period.
    const queryWindow = utcWindow(timeFrame);
    const { data, loading, error } = useQuery(OrganizationActivityStatsDocument, {
        variables: {
            input: {
                organizationIds: queriedOrgIds,
                from: queryWindow.from,
                to: queryWindow.to,
            },
        },
        skip: queriedOrgIds.length === 0,
    });
    const { data: membershipStatsData } = useQuery(OrganizationMembershipStatsDocument, {
        variables: {
            organizationIds: queriedOrgIds,
            from: queryWindow.from,
            to: queryWindow.to,
        },
        skip: queriedOrgIds.length === 0,
    });
    const { data: lmsStatsData } = useQuery(OrganizationLmsStatsDocument, {
        variables: { organizationIds: queriedOrgIds, from: queryWindow.from, to: queryWindow.to },
        skip: queriedOrgIds.length === 0,
    });

    const stats = data?.organizationActivityStats;
    /*
     * The one denominator for every rate here — the whole roster as of the window's end, accounts
     * and outstanding invitations together, from the same query as the numerators. Reading a
     * *current* member count instead is what let two rates on this page divide by different
     * populations.
     *
     * Invitations belong in it because they belong in the numerator too: somebody auto-enrolled is
     * on the newsletter from the moment they are enrolled, and `countActiveUsers` now counts the
     * ones who open or click. Dividing that numerator by account holders alone would measure two
     * different populations against each other.
     */
    const totalUsers = stats ? rosterTotal(stats) : undefined;
    const membershipStats = membershipStatsData?.organizationMembershipStats;
    const lmsStats = lmsStatsData?.organizationLmsStats;
    const orgs = orgData?.organizations ?? [];
    // In the dropdown's order, so All picked either way sends the same list, and the collapsed list
    // and the export's sheets read as the dropdown does.
    const orderedOrgs = flattenOrgOptions(orgs).map(({ org }) => org);
    const allSelected = isAllSelected(queriedOrgIds, orgs);
    const selection = new Set(queriedOrgIds);
    const selectedOrgs = orderedOrgs.filter((org) => selection.has(org.id));

    /**
     * Several organizations: an Excel workbook, the combined numbers first and then a sheet per
     * organization, each holding the rows the CSV export writes (ATH-247). Each organization is read
     * on its own, so its sheet is what the page shows when it is picked alone: one request apiece,
     * two at a time, each retried if it is refused.
     */
    const handleExportStatsExcel = async () => {
        if (!stats) return;
        const total = selectedOrgs.length;
        let done = 0;
        setExportProgress({ done, total });
        try {
            const range = { from: queryWindow.from, to: queryWindow.to };
            const rowsFor = (
                orgStats: NonNullable<typeof stats>,
                orgLms: typeof lmsStats,
                orgMembership: typeof membershipStats,
            ) => [
                STATS_CSV_HEADERS,
                ...buildStatsCsvRows({
                    stats: orgStats,
                    lmsStats: orgLms,
                    membershipStats: orgMembership,
                    timeFrame,
                    includeTagViews,
                    includeTopicViews,
                }),
            ];
            const perOrg: Sheet[] = [];
            for (let start = 0; start < total; start += EXPORT_CONCURRENCY) {
                const batch = selectedOrgs.slice(start, start + EXPORT_CONCURRENCY);
                perOrg.push(
                    ...(await Promise.all(
                        batch.map(async (org) => {
                            const organizationIds = [org.id];
                            const { data: exported } = await withRetries(() =>
                                apollo.query({
                                    query: OrganizationStatsExportDocument,
                                    variables: { input: { organizationIds, ...range }, organizationIds, ...range },
                                }),
                            );
                            if (!exported) throw new Error(`No stats for ${org.name}`);
                            done += 1;
                            setExportProgress({ done, total });
                            return {
                                name: orgLabel(org, orgs),
                                rows: rowsFor(
                                    exported.organizationActivityStats,
                                    exported.organizationLmsStats,
                                    exported.organizationMembershipStats,
                                ),
                            };
                        }),
                    )),
                );
            }
            downloadXlsx(`stats-${new Date().toISOString().slice(0, 10)}`, [
                { name: allSelected ? "All" : "Combined", rows: rowsFor(stats, lmsStats, membershipStats) },
                ...perOrg,
            ]);
        } catch {
            toast.error("Couldn't build the Excel export. Try again in a minute.");
        } finally {
            setExportProgress(undefined);
        }
    };

    const handleExportStatsCsv = () => {
        if (!stats) return;
        exportCsv(
            "stats",
            STATS_CSV_HEADERS,
            buildStatsCsvRows({ stats, lmsStats, membershipStats, timeFrame, includeTagViews, includeTopicViews }),
        );
    };

    /**
     * Low view counts read awkwardly in a report handed to an organization, so each video section
     * can export without them. The page itself keeps showing them; only the exports drop them.
     */
    const viewsToggle = (
        id: ReportSectionId,
        label: string,
        checked: boolean,
        onChange: (checked: boolean) => void,
    ) => (
        <span className="ml-auto inline-flex items-center gap-2">
            <Checkbox
                id={`${id}-views`}
                checked={checked}
                disabled={isExportingPdf}
                onCheckedChange={(value) => onChange(value === true)}
                aria-label={`Include views in ${label} export`}
            />
            <Label htmlFor={`${id}-views`}>Include views</Label>
        </span>
    );

    return (
        <Container className="p-6">
            <SectionHeader
                eyebrow="Discover your data"
                title="Analytics Reports"
                description="Uncover trends about your data"
            />

            <div className="mb-6 max-w-xs">
                <OrganizationScopeSelect
                    value={selectedOrgIds}
                    onValueChange={setSelectedOrgIds}
                    label={scopeLabel(selectedOrgIds, orgs)}
                    offerAll={offersAll(orgs)}
                    className="w-full"
                    aria-label="Organization"
                />
            </div>

            <div className="mb-6">
                <TimeFrameSelector value={timeFrame} onChange={setTimeFrame} />
            </div>

            {stats && (
                <div className="mb-6 flex flex-wrap items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={isExportingPdf}
                        onClick={() => setSelectedKeys(new Set(ALL_REPORT_KEYS))}
                    >
                        Select All
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={selectedKeys.size === 0 || isExportingPdf}
                        onClick={() => setSelectedKeys(new Set())}
                    >
                        Clear All
                    </Button>
                    <Button
                        variant="default"
                        size="sm"
                        disabled={selectedKeys.size === 0 || isExportingPdf}
                        onClick={() => void handleExportPdf()}
                    >
                        {isExportingPdf ? "Exporting…" : `Export PDF (${includedSectionCount(selectedKeys)})`}
                    </Button>
                    {queriedOrgIds.length > 1 ? (
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={exportProgress !== undefined}
                            onClick={() => void handleExportStatsExcel()}
                        >
                            {exportProgress
                                ? `Exporting ${exportProgress.done} of ${exportProgress.total}…`
                                : "Export Excel"}
                        </Button>
                    ) : (
                        <Button variant="outline" size="sm" onClick={handleExportStatsCsv}>
                            Export CSV
                        </Button>
                    )}
                </div>
            )}

            {stats && (
                <ReportContentPanel
                    brand={brand}
                    onBrandChange={setBrand}
                    narrative={narrative}
                    onNarrativeChange={setNarrative}
                />
            )}

            {queriedOrgIds.length === 0 && (
                <Text size="sm" muted>
                    Select an organization to view its activity stats.
                </Text>
            )}

            {queriedOrgIds.length > 0 && loading && !data && (
                <Text size="sm" muted>
                    Loading stats…
                </Text>
            )}

            {queriedOrgIds.length > 0 && error && (
                <Text size="sm" className="text-destructive">
                    Failed to load stats.
                </Text>
            )}

            {stats && (
                <div>
                    <div className="mb-6">
                        <Heading level={2}>{scopeLabel(queriedOrgIds, orgs) ?? "Organization"}</Heading>
                        <Body className="text-text-caption">{timeFrame.label}</Body>
                        {queriedOrgIds.length > 1 && !allSelected && (
                            <details className="mt-1">
                                <summary className="text-text-caption cursor-pointer text-sm">
                                    Show organizations
                                </summary>
                                <ul className="mt-1 list-disc pl-5 text-sm">
                                    {selectedOrgs.map((org) => (
                                        <li key={org.id}>{orgLabel(org, orgs)}</li>
                                    ))}
                                </ul>
                            </details>
                        )}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2" style={{ marginTop: 6, marginBottom: 6 }}>
                            {sectionCheckbox("users-actions", "Users & Actions")}
                            <Heading level={3}>Users & Actions</Heading>
                        </div>
                        <StatCardGroup className="mb-6 w-full">
                            <StatCard
                                label="Total users"
                                action={itemCheckbox("total-users", "Total users")}
                                value={totalUsers ?? "—"}
                                detail={`members as of ${timeFrame.label}`}
                            />
                            <StatCard
                                label="Active users"
                                action={itemCheckbox("active-users", "Active users")}
                                value={stats.activeUsers}
                                detail={`users who completed at least one action in ${timeFrame.label}`}
                            />
                            <StatCard
                                label="Active user rate"
                                action={itemCheckbox("active-user-rate", "Active user rate")}
                                value={totalUsers ? pct(stats.activeUsers / totalUsers) : "—"}
                                detail={`${ofMembers(stats.activeUsers, totalUsers)} total users are active in ${timeFrame.label}`}
                            />
                            <StatCard
                                label="Total actions"
                                action={itemCheckbox("total-actions", "Total actions")}
                                value={stats.totalActions}
                                detail={`in ${timeFrame.label}`}
                            />
                            <StatCard
                                label="Avg actions per user"
                                action={itemCheckbox("avg-actions-per-user", "Avg actions per user")}
                                value={stats.activeUsers ? (stats.totalActions / stats.activeUsers).toFixed(1) : "—"}
                                detail="per active user"
                            />
                        </StatCardGroup>
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2" style={{ marginTop: 6, marginBottom: 6 }}>
                            {sectionCheckbox("email-newsletter", "Email Newsletter")}
                            <Heading level={3}>Email Newsletter</Heading>
                        </div>
                        <StatCardGroup className="mb-6 w-full">
                            <StatCard
                                label="Email reach rate"
                                action={itemCheckbox("email-reach-rate", "Email reach rate")}
                                value={pct(stats.emailReachRate)}
                                detail={`${ofMembers(stats.emailOpeners, totalUsers)} opened ≥1 email`}
                            />
                            <StatCard
                                label="Email click rate"
                                action={itemCheckbox("email-click-rate", "Email click rate")}
                                value={pct(stats.emailClickRate)}
                                detail={`${ofMembers(stats.emailClickers, totalUsers)} clicked ≥1 link`}
                            />
                        </StatCardGroup>
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2" style={{ marginTop: 6, marginBottom: 6 }}>
                            {sectionCheckbox("coaching", "Coaching")}
                            <Heading level={3}>Coaching</Heading>
                        </div>
                        <StatCardGroup className="mb-6 w-full">
                            <StatCard
                                label="Coaching users"
                                action={itemCheckbox("coaching-users", "Coaching users")}
                                value={stats.coachingUsers}
                                detail={`messaged their coach in ${timeFrame.label}`}
                            />
                            <StatCard
                                label="Coaching sessions"
                                action={itemCheckbox("coaching-sessions", "Coaching sessions")}
                                value={stats.coachingSessions}
                                detail={`continuous conversations in ${timeFrame.label}`}
                            />
                            <StatCard
                                label="Coaching messages"
                                action={itemCheckbox("coaching-messages", "Coaching messages")}
                                value={stats.coachingMessages}
                                detail={`sent in ${timeFrame.label}, both sides`}
                            />
                            <StatCard
                                label="Messages to coach"
                                action={itemCheckbox("messages-to-coach", "Messages to coach")}
                                value={stats.messagesToCoach}
                                detail="the learner's half of those"
                            />
                        </StatCardGroup>
                    </div>
                    {lmsStats && (
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2" style={{ marginTop: 6, marginBottom: 6 }}>
                                {sectionCheckbox("lms", "Learning Management System")}
                                <Heading level={3}>Learning Management System</Heading>
                            </div>
                            <StatCardGroup className="mb-6 w-full">
                                <StatCard
                                    label="Users in progress"
                                    action={itemCheckbox("users-in-progress", "Users in progress")}
                                    value={lmsStats.usersWithActiveAssignments}
                                    detail="worked on a course this period, including any who finished"
                                />
                                <StatCard
                                    label="Users who started a course"
                                    action={itemCheckbox("users-started-course", "Users who started a course")}
                                    value={lmsStats.usersWhoStartedCourses}
                                    detail="began a course this period"
                                />
                                <StatCard
                                    label="Users who completed a course"
                                    action={itemCheckbox("users-completed-course", "Users who completed a course")}
                                    value={lmsStats.usersWhoCompletedCourses}
                                    detail="finished every item in a course this period"
                                />
                            </StatCardGroup>
                        </div>
                    )}

                    {stats.videoViewSummaries.length > 0 && (
                        <>
                            <div className="min-w-0 flex-1">
                                <div className="mt-6 mb-6 flex items-center gap-2">
                                    {sectionCheckbox("top-video-tags", "Top Video Tags")}
                                    <Heading level={3}>Top Video Tags</Heading>
                                    {viewsToggle(
                                        "top-video-tags",
                                        "Top Video Tags",
                                        includeTagViews,
                                        setIncludeTagViews,
                                    )}
                                </div>
                                <Body className="text-text-caption">
                                    The most popular tags based on the video category.
                                </Body>
                                <DataTable
                                    columns={VIDEO_TAG_COLUMNS}
                                    rows={topVideoTags(stats.videoViewSummaries).sort((a, b) => b.views - a.views)}
                                    rowKey={(tag) => tag.tag}
                                />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="mt-6 mb-6 flex items-center gap-2">
                                    {sectionCheckbox("top-video-topics", "Top Video Topics")}
                                    <Heading level={3}>Top Video Topics</Heading>
                                    {viewsToggle(
                                        "top-video-topics",
                                        "Top Video Topics",
                                        includeTopicViews,
                                        setIncludeTopicViews,
                                    )}
                                </div>
                                <Body className="text-text-caption">The most popular videos on the platform.</Body>
                                <DataTable
                                    columns={VIDEO_TOPIC_COLUMNS}
                                    rows={[...stats.videoViewSummaries].slice(0, 5).sort((a, b) => b.views - a.views)}
                                    rowKey={(video) => video.contentId}
                                />
                            </div>
                        </>
                    )}

                    <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
                        <div className="min-w-0 flex-1">
                            <div className="bg-card min-w-0 rounded-lg border p-6">
                                <div className="mb-2 flex items-center gap-2">
                                    {sectionCheckbox("platforms-actions", "Platforms - Actions")}
                                    <Heading level={3}>Platforms - Actions</Heading>
                                </div>
                                <Body className="text-text-caption">Breakdown of total actions by platform.</Body>
                                {hasAnyValue(clientPlatforms(stats.byPlatform), (row) => row.total) ? (
                                    <PieChart
                                        showValues
                                        data={clientPlatforms(stats.byPlatform).map((row) => ({
                                            name: platformLabel(row.platform),
                                            value: row.total,
                                        }))}
                                    />
                                ) : (
                                    <NoChartData />
                                )}
                            </div>
                            <div className="bg-card mt-6 min-w-0 rounded-lg border p-6">
                                <div className="mb-2 flex items-center gap-2">
                                    {sectionCheckbox("platforms-users", "Platforms - Users")}
                                    <Heading level={3}>Platforms - Users</Heading>
                                </div>
                                <Body className="text-text-caption">
                                    Breakdown of how users are accessing the platform.
                                </Body>
                                {hasAnyValue(clientPlatforms(stats.usersByPlatform), (row) => row.total) ? (
                                    <PieChart
                                        showValues
                                        data={clientPlatforms(stats.usersByPlatform).map((row) => ({
                                            name: platformLabel(row.platform),
                                            value: row.total,
                                        }))}
                                    />
                                ) : (
                                    <NoChartData />
                                )}
                            </div>
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="bg-card min-w-0 rounded-lg border p-6">
                                <div className="mb-2 flex items-center gap-2">
                                    {sectionCheckbox("frequency-tiers", "Frequency Tiers")}
                                    <Heading level={3}>Frequency Tiers</Heading>
                                </div>
                                <Body className="text-text-caption">
                                    Active users grouped by how often they return.
                                </Body>
                                {hasAnyValue(stats.frequencyTiers, (row) => row.userCount) ? (
                                    <PieChart
                                        showValues
                                        data={stats.frequencyTiers.map((row) => ({
                                            name: frequencyTierLabelMap[row.tier] ?? row.tier,
                                            description: frequencyTierDescriptionMap[row.tier],
                                            value: row.userCount,
                                        }))}
                                    />
                                ) : (
                                    <NoChartData />
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="min-w-0 flex-1">
                        <div className="mt-6 mb-6 flex items-center gap-2">
                            {sectionCheckbox("engagement-buckets", "Engagement Buckets")}
                            <Heading level={3}>Engagement Buckets</Heading>
                        </div>
                        <Body className="text-text-caption">How learners are engaging with the platform.</Body>
                        {hasAnyValue(stats.byEngagementCategory, (row) => row.total) ? (
                            <BarChart
                                data={stats.byEngagementCategory.map((row) => ({
                                    name: engagementCategoryLabelMap[row.category] ?? row.category,
                                    value: row.total,
                                }))}
                                xAxisKey="name"
                                bars={[{ dataKey: "value", label: "Actions" }]}
                                colorByCategory
                            />
                        ) : (
                            <NoChartData />
                        )}
                    </div>

                    <div className="min-w-0 flex-1">
                        <div className="mt-6 mb-6 flex items-center gap-2">
                            {sectionCheckbox("actions-per-month", "Actions per Month")}
                            <Heading level={3}>Actions per Month</Heading>
                        </div>
                        <Body className="text-text-caption">Raw count of actions month-over-month.</Body>
                        {hasAnyValue(stats.byMonth, (row) => row.totalActions) ? (
                            <BarChart
                                data={stats.byMonth.map((row) => ({ ...row, month: formatMonthLabel(row.month) }))}
                                xAxisKey="month"
                                bars={[{ dataKey: "totalActions", label: "Total actions" }]}
                            />
                        ) : (
                            <NoChartData />
                        )}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="mt-6 mb-6 flex items-center gap-2">
                            {sectionCheckbox("actions-per-month-by-user", "Actions per Month by User")}
                            <Heading level={3}>Actions per Month by User</Heading>
                        </div>
                        <Body className="text-text-caption">
                            Compares total actions by active users. This tells us how many users are performing actions.
                        </Body>
                        {hasAnyValue(stats.byMonth, (row) => row.totalActions) ? (
                            <BarChart
                                data={stats.byMonth.map((row) => ({ ...row, month: formatMonthLabel(row.month) }))}
                                xAxisKey="month"
                                bars={[
                                    { dataKey: "totalActions", label: "Total actions" },
                                    { dataKey: "activeUsers", label: "Active users" },
                                ]}
                            />
                        ) : (
                            <NoChartData />
                        )}
                    </div>

                    {timeFrame.mode === "MONTH" &&
                        (() => {
                            const dailyData = dailyActions(stats.byDay, timeFrame.from).map((row) => ({
                                day: formatDayLabel(row.day),
                                total: row.total,
                            }));
                            return (
                                <div className="min-w-0 flex-1">
                                    <div className="mt-6 mb-6 flex items-center gap-2">
                                        {sectionCheckbox("daily-actions", "Daily Actions")}
                                        <Heading level={3}>Daily Actions</Heading>
                                    </div>
                                    <Body className="text-text-caption">
                                        Raw count of actions being performed per day.
                                    </Body>
                                    {hasAnyValue(dailyData, (row) => row.total) ? (
                                        <BarChart
                                            data={dailyData}
                                            xAxisKey="day"
                                            bars={[{ dataKey: "total", label: "Actions" }]}
                                        />
                                    ) : (
                                        <NoChartData />
                                    )}
                                </div>
                            );
                        })()}

                    <div className="min-w-0 flex-1">
                        <div className="mt-6 mb-6 flex items-center gap-2">
                            {sectionCheckbox("user-growth", "User Growth")}
                            <Heading level={3}>User Growth</Heading>
                        </div>
                        <Body className="text-text-caption">Raw count of user growth month-over-month.</Body>
                        {membershipStats && hasAnyValue(membershipStats, (row) => row.total) ? (
                            <BarChart
                                data={membershipStats.map((row) => ({
                                    month: formatMonthLabel(row.month),
                                    total: row.total,
                                }))}
                                xAxisKey="month"
                                bars={[{ dataKey: "total", label: "Total users" }]}
                            />
                        ) : (
                            <NoChartData />
                        )}
                    </div>
                </div>
            )}

            {stats && isExportingPdf && (
                <div
                    aria-hidden="true"
                    className="pointer-events-none fixed top-0"
                    style={{ left: OFFSCREEN_REPORT_LEFT_PX }}
                >
                    <PartnerReport
                        ref={reportRef}
                        brand={reportBrand(brand)}
                        organizationName={scopeLabel(queriedOrgIds, orgs) ?? "Organization"}
                        timeFrame={timeFrame}
                        narrative={narrative}
                        selection={selectedKeys}
                        stats={stats}
                        lmsStats={lmsStats}
                        membershipStats={membershipStats}
                        includeTagViews={includeTagViews}
                        includeTopicViews={includeTopicViews}
                    />
                </div>
            )}
        </Container>
    );
};
